import { create } from "zustand";
import { PlanOfCareDetails } from "../hooks/usePlanOfCareDetails";
import { DateTime } from "luxon";

export type BookingOutcome = {
  status: "Scheduled" | "Joined Waitlist" | "Skipped";
  id: string;
  appointmentTypeId: string;
  appointmentTypeName: string;
  appointmentDuration: number;
  providerId?: string;
  providerName?: string;
  start?: DateTime;
  caseStudyId?: string;
};

type PlanOfCareStore = {
  uniqueId: string | undefined;
  setUniqueId: (uniqueId: string) => void;
  totalWeeks: number;
  setTotalWeeks: (weeks: number) => void;
  gap: number;
  setGap: (gap: number) => void;
  currentPeriod: PlanOfCareDetails["planOfCares"][0]["periods"][0] | undefined;
  setCurrentPeriod: (
    period: PlanOfCareDetails["planOfCares"][0]["periods"][0]
  ) => void;
  selectedProvider: {
    label: string;
    value: string;
  };
  setSelectedProvider: (provider: { label: string; value: string }) => void;
  providerOptions: { label: string; value: string }[];
  setProviderOptions: (options: { label: string; value: string }[]) => void;
  selectedAppointment: {
    id: string;
    appointmentTypeId: string;
    appointmentTypeName: string;
    appointmentDuration: number;
  };
  setSelectedAppointment: (appointment: {
    id: string;
    appointmentTypeId: string;
    appointmentTypeName: string;
    appointmentDuration: number;
  }) => void;
  date: DateTime | undefined;
  setDate: (date: DateTime | undefined) => void;
  bookableDays: DateTime[];
  setBookableDays: (days: DateTime[]) => void;
  bookingOutcomes: BookingOutcome[];
  appendBookingOutcomes: (outcome: BookingOutcome) => void;
  removeBookingOutcome: (appointmentId: string) => void;
  discharged: boolean;
  setDischarged: (discharged: boolean) => void;
  authorization: { startDate: string; endDate: string; visitLimit: number } | undefined;
  setAuthorization: (authorization: { startDate: string; endDate: string; visitLimit: number } | undefined) => void;
  authorizationsByType: Record<string, { startDate: string; endDate: string; visitLimit: number | null }[]> | undefined;
  setAuthorizationsByType: (authorizationsByType: Record<string, { startDate: string; endDate: string; visitLimit: number | null }[]> | undefined) => void;
  scheduledVisitDates: string[];
  setScheduledVisitDates: (dates: string[]) => void;
  ignoreAuthorization: boolean;
  setIgnoreAuthorization: (ignore: boolean) => void;
  caseStudyId: string | undefined;
  setCaseStudyId: (caseStudyId: string | undefined) => void;
  planOfCareId: string | undefined;
  setPlanOfCareId: (planOfCareId: string | undefined) => void;
  crossCaseStudyBlockedDates: Set<string>;
  setCrossCaseStudyBlockedDates: (dates: Set<string>) => void;
  debugMode: boolean;
  setDebugMode: (debugMode: boolean) => void;
  bookableDayReasons: Map<string, string>;
  setBookableDayReasons: (reasons: Map<string, string>) => void;
};

export const usePlanOfCareStore = create<PlanOfCareStore>((set) => ({
  uniqueId: undefined,
  setUniqueId: (uniqueId: string) => set({ uniqueId }),
  totalWeeks: 0,
  setTotalWeeks: (weeks: number) => set({ totalWeeks: weeks }),
  gap: 0,
  setGap: (gap: number) => set({ gap }),
  currentPeriod: undefined,
  setCurrentPeriod: (period: PlanOfCareDetails["planOfCares"][0]["periods"][0]) =>
    set({ currentPeriod: period }),
  selectedProvider: { label: "All Providers", value: "all" },
  setSelectedProvider: (provider: { label: string; value: string }) =>
    set({ selectedProvider: provider }),
  providerOptions: [],
  setProviderOptions: (options: { label: string; value: string }[]) =>
    set({ providerOptions: options }),
  selectedAppointment: {
    id: "",
    appointmentTypeId: "",
    appointmentTypeName: "",
    appointmentDuration: 0
  },
  setSelectedAppointment: (appointment: {
    id: string;
    appointmentTypeId: string;
    appointmentTypeName: string;
    appointmentDuration: number;
  }) => set({ selectedAppointment: appointment }),
  date: undefined,
  setDate: (date: DateTime | undefined) => set({ date }),
  bookableDays: [],
  setBookableDays: (days: DateTime[]) => set({ bookableDays: days }),
  bookingOutcomes: [],
  appendBookingOutcomes: (outcome: BookingOutcome) =>
    set((state) => ({ bookingOutcomes: [...state.bookingOutcomes, outcome] })),
  removeBookingOutcome: (appointmentId: string) =>
    set((state) => ({
      bookingOutcomes: state.bookingOutcomes.filter(
        (outcome) => outcome.id !== appointmentId
      )
    })),
  discharged: false,
  setDischarged: (discharged: boolean) => set({ discharged }),
  authorization: undefined,
  setAuthorization: (authorization) => set({ authorization }),
  authorizationsByType: undefined,
  setAuthorizationsByType: (authorizationsByType) => set({ authorizationsByType }),
  scheduledVisitDates: [],
  setScheduledVisitDates: (dates) => set({ scheduledVisitDates: dates }),
  ignoreAuthorization: false,
  setIgnoreAuthorization: (ignore) => set({ ignoreAuthorization: ignore }),
  caseStudyId: undefined,
  setCaseStudyId: (caseStudyId: string | undefined) => set({ caseStudyId }),
  planOfCareId: undefined,
  setPlanOfCareId: (planOfCareId: string | undefined) => set({ planOfCareId }),
  crossCaseStudyBlockedDates: new Set<string>(),
  setCrossCaseStudyBlockedDates: (dates: Set<string>) => set({ crossCaseStudyBlockedDates: dates }),
  debugMode: false,
  setDebugMode: (debugMode: boolean) => set({ debugMode }),
  bookableDayReasons: new Map<string, string>(),
  setBookableDayReasons: (reasons: Map<string, string>) => set({ bookableDayReasons: reasons }),
}));
